import {
  CUSTOMER_ID,
  ORDERDATA,
  CURRENT_CUSTOMIZING_ID,
  CUSTOMER_DETAILS,
  BUTCHER_DETAILS,
} from '@helpers/Constants';
import { ButcherDetails, NewOrderInitiationProps } from '@helpers/Models';

/**
 * Service to manage customer order data, IDs, and details within localStorage.
 */
export const CustomerOrderService = {
  /**
   * Stores the customer ID in localStorage.
   * @param customerId - The numerical ID of the customer.
   */
  setCustomerId(customerId: number) {
    // Store as string since localStorage only accepts strings
    localStorage.setItem(CUSTOMER_ID, customerId.toString());
  },

  /**
   * Retrieves the customer ID from localStorage.
   * @returns The customer ID as a number, or 0 if not found.
   */
  getCustomerId(): number {
    const id = localStorage.getItem(CUSTOMER_ID);
    // Convert string back to number for application logic
    return Number(id);
  },

  /**
   * Removes the customer ID from localStorage.
   */
  clearCustomerId() {
    localStorage.removeItem(CUSTOMER_ID);
  },

  /**
   * Retrieves and parses the entire order data object from localStorage.
   * @returns A record mapping customer IDs to their respective order data.
   */
  getParsedOrderData(): Record<string, any> {
    const stored = localStorage.getItem(ORDERDATA);
    // Return empty object if no data exists to prevent null errors
    return stored ? JSON.parse(stored) : {};
  },

  /**
   * Persists the entire order data object to localStorage.
   * @param orderData - The complete order data record to save.
   */
  setParsedOrderData(orderData: Record<string, any>) {
    localStorage.setItem(ORDERDATA, JSON.stringify(orderData));
  },

  /**
   * Retrieves the ID of the specific item currently being customized.
   * @returns The customization ID as a number.
   */
  getcurrentCustomizingId(): number {
    const id = localStorage.getItem(CURRENT_CUSTOMIZING_ID);
    return Number(id);
  },

  /**
   * Retrieves the currently active customer's details.
   * @returns The customer details object, or an empty object if not found or invalid.
   */
  getCustomerDetails(): NewOrderInitiationProps {
    const stored = localStorage.getItem(CUSTOMER_DETAILS);
    try {
      // Attempt to parse existing data
      return stored
        ? (JSON.parse(stored) as NewOrderInitiationProps)
        : ({} as NewOrderInitiationProps);
    } catch {
      // Fallback to empty object if JSON is malformed
      return {} as NewOrderInitiationProps;
    }
  },

  /**
   * Stores the active customer's details in localStorage.
   * @param details - The customer details object to persist.
   */
  setCustomerDetails(details: NewOrderInitiationProps): void {
    localStorage.setItem(CUSTOMER_DETAILS, JSON.stringify(details));
  },

  /**
   * Stores butcher information for the current order.
   * @param butcherDetails - The butcher details object.
   */
  setButcherDetails(butcherDetails: ButcherDetails): void {
    // Prevent saving null or undefined data
    if (!butcherDetails) return;
    localStorage.setItem(BUTCHER_DETAILS, JSON.stringify(butcherDetails));
  },

  /**
   * Retrieves butcher information from localStorage.
   * @returns The butcher details object, or null if not found.
   */
  getButcherDetails(): ButcherDetails | null {
    const stored = localStorage.getItem(BUTCHER_DETAILS);
    return stored ? JSON.parse(stored) : null;
  },
};
